import type { FingeringGroup } from '../../hooks/usePdfStateQueue';
import type { FingeringSizeConfig } from './fingeringSizes';

// Vertical offset (CSS px) between the staff edge and the first
// fingering digit; baked into the canvas-expansion math.
export const FIRST_DIGIT_OFFSET = 7;

// CSS-pixel buffer added past the outermost digit so glyph
// antialiasing and stroke widths aren't visually clipped at the
// top/bottom edge of each per-system canvas. Kept small so gaps
// between system rows stay visually tight.
export const OVERLAY_EXPANSION_PADDING = 5;

export interface OverlayExpansionOptions {
  /** Display px per image px, vertically. */
  scaleY: number;
  /** Displayed image height, in px. */
  displayHeight: number;
  showFingerings: boolean;
  showAltFingerings: boolean;
}

/**
 * Single source of truth for the per-system canvas expansion (the
 * vertical padding above/below the system PNG that gives fingering
 * pills somewhere to live). The SAME values must drive both the
 * container's CSS `paddingTop`/`paddingBottom` and the SVG's
 * `viewBox` totalHeight + `coordinateOffsetY` — otherwise the SVG
 * (rendered with `width/height="100%"` + `preserveAspectRatio="none"`)
 * stretches its viewBox vertically to fit a CSS box of a different
 * height, which uniformly scales bbox y positions by ≠ 1 with the
 * viewBox top as the fixed point. The visible symptom is bboxes
 * and pitch letters that drift away from the noteheads as you go
 * further from the top of each system, with the drift direction
 * depending on which Math.max winner each computation picked.
 */
export const computeOverlayExpansion = (
  fingeringGroups: FingeringGroup[],
  sizeConfig: FingeringSizeConfig,
  options: OverlayExpansionOptions
): { maxExpansionUp: number; maxExpansionDown: number } => {
  const { spacing, gapBetween } = sizeConfig;
  const { scaleY, displayHeight, showFingerings, showAltFingerings } = options;

  let maxExpansionUp = 0;
  let maxExpansionDown = 0;
  fingeringGroups.forEach((fingeringGroup) => {
    const fingeringNumList = fingeringGroup.fingeringNumList;
    const { upward } = fingeringGroup.coordinate;
    // Hidden digits take no room, so the gap closes when they are
    // switched off.
    const mainCount = showFingerings
      ? fingeringNumList.filter((fn) => fn.mainFingering > 0).length
      : 0;
    const altCount =
      showFingerings && showAltFingerings
        ? fingeringNumList.filter((fn) => fn.altFingering > 0).length
        : 0;
    if (mainCount === 0 && altCount === 0) return;
    // When both subgroups are present we insert `gapBetween` of
    // padding between the main pill and the alt pill, which pushes
    // the outermost alt digit that much further from the staff.
    const altGap = mainCount > 0 && altCount > 0 ? gapBetween : 0;
    const altSideExpansion =
      FIRST_DIGIT_OFFSET +
      altCount * spacing +
      altGap +
      OVERLAY_EXPANSION_PADDING;
    const mainSideExpansion =
      Math.max(0, (mainCount - 1) * spacing) + OVERLAY_EXPANSION_PADDING;
    if (upward) {
      maxExpansionUp = Math.max(maxExpansionUp, altSideExpansion);
      maxExpansionDown = Math.max(maxExpansionDown, mainSideExpansion);
    } else {
      maxExpansionUp = Math.max(maxExpansionUp, mainSideExpansion);
      maxExpansionDown = Math.max(maxExpansionDown, altSideExpansion);
    }

    // The estimate above keeps the usual row spacing, but it assumes the
    // anchor sits on the image edge and leaves the main digits out of the
    // alt side. A tall stack can still hang past the image, so the drawn
    // pills set the floor.
    const { top, bottom } = stackExtent(
      fingeringStackGeometry(fingeringGroup, sizeConfig, 1, scaleY),
      altCount > 0
    );
    if (top < 0) {
      maxExpansionUp = Math.max(
        maxExpansionUp,
        Math.ceil(-top) + OVERLAY_EXPANSION_PADDING
      );
    }
    if (bottom > displayHeight) {
      maxExpansionDown = Math.max(
        maxExpansionDown,
        Math.ceil(bottom - displayHeight) + OVERLAY_EXPANSION_PADDING
      );
    }
  });
  return { maxExpansionUp, maxExpansionDown };
};

export interface PillBox {
  y: number;
  height: number;
}

/** One drawn digit and the y of its center. */
export interface StackDigit {
  value: number;
  y: number;
}

/** Where one group's digits and pills are drawn, in display px. */
export interface FingeringStackGeometry {
  x: number;
  mains: StackDigit[];
  alts: StackDigit[];
  mainPill: PillBox | null;
  altPill: PillBox | null;
}

/**
 * One group's digit stack. Main digits start at the anchor and grow away
 * from the staff; alt digits continue past them, `gapBetween` further.
 * `offsetY` is the canvas padding above the image.
 */
export const fingeringStackGeometry = (
  fingeringGroup: FingeringGroup,
  sizeConfig: FingeringSizeConfig,
  scaleX: number,
  scaleY: number,
  offsetY = 0
): FingeringStackGeometry => {
  const { spacing, gapBetween, numHeight, padY } = sizeConfig;
  const { x, y, upward } = fingeringGroup.coordinate;
  const mainValues = fingeringGroup.fingeringNumList
    .map((fn) => fn.mainFingering)
    .filter((value) => value > 0);
  const altValues = fingeringGroup.fingeringNumList
    .map((fn) => fn.altFingering)
    .filter((value) => value > 0);
  const mainCount = mainValues.length;
  const altCount = altValues.length;
  const direction = upward ? -1 : 1;
  const anchorY = y * scaleY + offsetY + direction * FIRST_DIGIT_OFFSET;
  const altStart =
    Math.max(mainCount, 1) * spacing +
    (mainCount > 0 && altCount > 0 ? gapBetween : 0);
  const mains = mainValues.map((value, i) => ({
    value,
    y: anchorY + direction * i * spacing,
  }));
  const alts = altValues.map((value, i) => ({
    value,
    y: anchorY + direction * (altStart + i * spacing),
  }));
  const pill = (digits: StackDigit[]): PillBox | null => {
    if (digits.length === 0) return null;
    const ys = digits.map((digit) => digit.y);
    const top = Math.min(...ys) - padY - numHeight / 2;
    const bottom = Math.max(...ys) + padY + numHeight / 2;
    return { y: top, height: bottom - top };
  };
  return {
    x: x * scaleX,
    mains,
    alts,
    mainPill: pill(mains),
    altPill: pill(alts),
  };
};

const stackExtent = (
  geometry: FingeringStackGeometry,
  withAlts: boolean
): { top: number; bottom: number } => {
  const pills = [geometry.mainPill, withAlts ? geometry.altPill : null].filter(
    (pill): pill is PillBox => pill !== null
  );
  if (pills.length === 0) return { top: 0, bottom: 0 };
  return {
    top: Math.min(...pills.map((pill) => pill.y)),
    bottom: Math.max(...pills.map((pill) => pill.y + pill.height)),
  };
};
